import React, {
  useMemo,
} from 'react';
import styled from 'styled-components';
import 'react-image-lightbox/style.css';
import { useSelector } from 'react-redux';
import { selectLocale } from 'models/localization';
import { Booth, DesignElement } from 'models/cms/booths';
import {
  FETCH_ASSETS_URL,
} from 'appenv';

import { ReactComponent as ColourChange } from 'assets/booths/923-customization/option1/colourchange.svg';

import BusinessPopover from 'pages/booths/components/BusinessPopover';
import BoothContainer from '../components/BoothContainer';
import GenericCMSBoothBanner from '../components/GenericCMSBoothBanner';
import useEmailContactsMemo from '../useEmailContactsMemo';

const BoothBackgroundImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option1/bg.png`;
const LightImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option1/lighting-effect.png`;

const CounterImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option1/counter.png`;

const CounterGraphicImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option1/counter-graphic.png`;

const FlowerImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/flower.png`;

const RepresentativeImage = `${FETCH_ASSETS_URL}/assets/booths/923-customization/option1/representative.png`;

const BackgroundImage = styled.div`
  height: 110%;
  width: 130%;
  position: absolute;
  left: -150px;
  background: url(${BoothBackgroundImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
`;

const OverheadBannerContainer = styled.div`
  position: absolute;
  width: 300px;
  height: 160px;
  left: calc((100% - 261px) / 2);
  top: 9px;
`;

const LightBox = styled.img`
  position: absolute;
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 593px;
  top: 277px;
  left: 190px;
  z-index: 1;
  pointer-events: none;
`;

const BackgroundContainer = styled.div`
  width: 100%;
  height: 100%;
  position: absolute;
  left: 20px;
`;

const BackdropBannerContainer = styled.div`
  height: 280px;
  width: 230px;
  position: absolute;
  bottom: 9px;
  left: 516px;
`;

const PullUpBannerContainer = styled.div`
  height: 265px;
  width: 135px;
  position: absolute;
  bottom: -30px;
  right: -52px;
  z-index: 2;
`;

const TvBannerContainer = styled.div`
  height: 148px;
  top: 319px;
  left: 52px;
  width: 262px;
  position: absolute;
  z-index: 2;
`;

const TvBackgroundContainer = styled.div`
  height: 147px;
  top: 321px;
  left: 52px;
  width: 261px;
  position: absolute;
  background: #e9e9e9;
`;

const CounterBannerContainer = styled.div`
  height: 135px;
  bottom: -40px;
  right: 395px;
  width: 231px;
  position: absolute;
  z-index: 3;
`;

const FlowerContainer = styled.div`
  background: url(${FlowerImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 60px;
  bottom: 84px;
  right: 382px;
  width: 65px;
  z-index: 3;
`;

const CounterBackground = styled.div`
  background: url(${CounterImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 140px;
  bottom: -40px;
  right: 390px;
  width: 240px;
  z-index: 2;
`;

const CounterGraphic = styled.div`
  background: url(${CounterGraphicImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  height: 60px;
  bottom: -42px;
  right: 395px;
  width: 93px;
  z-index: 3;
  pointer-events: none;
`;

const BusinessPopoverContainer = styled.div`
  position: absolute;
  top: -20px;
  right: -150px;
  width: 340px;
`;

const BackgroundColorContainer = styled(ColourChange)`
  height: 602px;
  width: 670px;
  position: absolute;
  left: 187px;
  bottom: -120px;
  fill: ${({ color }) => color || '#622991'};
`;

const RepresentativeContainer = styled.div`
  background: url(${RepresentativeImage});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  position: absolute;
  width: 80px;
  height: 185px;
  right: 430px;
  bottom: 70px;
`;

export interface BannerProps {
  booth: Booth;
  handleClick: (type: string, isCMSElement: boolean) => void;
  isPreview?: boolean;
}

const Banner: React.FC<BannerProps> = ({
  booth,
  handleClick = () => null,
  isPreview = false,
}: BannerProps) => {
  const locale = useSelector(selectLocale);
  const backgroundColor: string = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'background_color')?.color, [booth.designElements]);
  const overheadBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'overhead_banner'), [booth.designElements]);
  const backdrop: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'backdrop'), [booth.designElements]);
  const counter: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'counter'), [booth.designElements]);
  const pullUpBanner: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode?.name === 'pull_up_banner'), [booth.designElements]);
  const tv: DesignElement = useMemo(() => (booth.designElements || []).find((designNode) => designNode.name === 'tv'), [booth.designElements]);
  const tabs = booth?.rootContentElement?.tabs || [];
  const emailContacts = useEmailContactsMemo(tabs);

  return (
    <BoothContainer needOffset={!isPreview}>
      <BackgroundContainer>
        <BackgroundImage />
      </BackgroundContainer>
      <BackgroundColorContainer color={backgroundColor} />
      <TvBackgroundContainer />
      <BusinessPopoverContainer>
        <BusinessPopover
          boothId={booth.id}
          boothName={booth.name[locale]}
          emailContacts={emailContacts}
          meetupUrl={booth.meetupEnabled ? booth.appointmentBookingUrl : ''}
          livestreams={booth.livestreams}
          videoCallUrl={booth.videoCallUrl}
        />
      </BusinessPopoverContainer>
      <OverheadBannerContainer onClick={() => handleClick(overheadBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={overheadBanner}
          onBannerClick={handleClick as any}
        />
      </OverheadBannerContainer>
      <LightBox src={LightImage} />
      <BackdropBannerContainer onClick={() => handleClick(backdrop.name, true)}>
        <GenericCMSBoothBanner
          bannerData={backdrop}
          onBannerClick={handleClick as any}
        />
      </BackdropBannerContainer>
      <PullUpBannerContainer onClick={() => handleClick(pullUpBanner.name, true)}>
        <GenericCMSBoothBanner
          bannerData={pullUpBanner}
          onBannerClick={handleClick as any}
        />
      </PullUpBannerContainer>
      <TvBannerContainer onClick={() => handleClick(tv.name, true)}>
        <GenericCMSBoothBanner
          autoPlayVideo={!isPreview}
          bannerData={tv}
          onBannerClick={handleClick as any}
        />
      </TvBannerContainer>
      <FlowerContainer />
      <CounterBackground />
      <CounterBannerContainer onClick={() => handleClick(counter.name, true)}>
        <GenericCMSBoothBanner
          bannerData={counter}
          onBannerClick={handleClick as any}
        />
      </CounterBannerContainer>
      <CounterGraphic />
      {!isPreview && (
        <RepresentativeContainer />
      )}
    </BoothContainer>
  );
};

export default Banner;
